import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import { useGlobalContext } from "../../ContextApi/GlobalContext";

// --- Validation Schemas ---
const loginSchema = yup.object().shape({
  email: yup.string().email("Invalid email format").required("Email is required"),
  password: yup.string().min(6, "Min 6 characters").required("Password is required"),
});

const signupSchema = yup.object().shape({
  name: yup.string().min(3, "Name must be at least 3 chars").required("Full Name is required"),
  email: yup.string().email("Invalid email format").required("Email is required"),
  password: yup.string().min(6, "Min 6 characters").required("Password is required"),
});

const forgotEmailSchema = yup.object().shape({
  email: yup.string().email("Invalid email format").required("Email is required"),
});

const otpSchema = yup.object().shape({
  otp: yup.string().length(6, "OTP must be 6 digits").required("OTP is required"),
});

const resetPasswordSchema = yup.object().shape({
  password: yup.string().min(6, "Min 6 characters").required("New Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const AuthModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { dispatch } = useGlobalContext();

  // State for Views: 'login', 'signup', 'forgot_email', 'verify_otp', 'reset_password'
  const [currentView, setCurrentView] = useState("login");
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState(""); // Store email for OTP flow

  // Dynamic Schema Resolver
  const getSchema = (view) => {
    switch (view) {
      case "signup": return signupSchema;
      case "forgot_email": return forgotEmailSchema;
      case "verify_otp": return otpSchema;
      case "reset_password": return resetPasswordSchema;
      default: return loginSchema;
    }
  };

  // Form Hooks
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    setValue
  } = useForm({
    resolver: yupResolver(getSchema(currentView)),
  });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      reset();
      setCurrentView("login");
      setRecoveryEmail("");
    }
  }, [isOpen, reset]);

  // Helper to switch views safely
  const switchView = (view) => {
    clearErrors();
    reset();
    setCurrentView(view);
  };

  // --- HANDLER: Google Login (Only for Login/Signup) ---
  const handleGoogleLogin = async () => {
    setLoader(true);
    try {
      setTimeout(() => {
        setLoader(false);
        dispatch({ authToken: "mock_google_token_123" });
        onClose();
        Swal.fire({
          icon: "success",
          title: "Google Login Success!",
          text: `Welcome to Eventix.`,
          confirmButtonColor: "#0B1F33",
          timer: 1500,
        });
        navigate("/dashboard");
      }, 1500);
    } catch (error) {
      setLoader(false);
      Swal.fire({ icon: "error", title: "Google Login Failed", text: error.message });
    }
  };

  // --- MAIN FORM SUBMISSION HANDLER ---
  const onSubmit = async (data) => {
    setLoader(true);

    // Simulate API Latency
    setTimeout(() => {
      setLoader(false);

      // --- LOGIC: FORGOT PASSWORD FLOW ---
      if (currentView === "forgot_email") {
        setRecoveryEmail(data.email);
        Swal.fire({ icon: "success", title: "OTP Sent", text: `Code sent to ${data.email}`, confirmButtonColor: "#0B1F33", timer: 1500 });
        switchView("verify_otp");
        return;
      }

      if (currentView === "verify_otp") {
        // Mock OTP Check
        if (data.otp === "123456") {
          Swal.fire({ icon: "success", title: "Verified", text: "OTP Verified Successfully", confirmButtonColor: "#0B1F33", timer: 1000 });
          switchView("reset_password");
        } else {
          Swal.fire({ icon: "error", title: "Invalid OTP", text: "Try 123456", confirmButtonColor: "#0B1F33" });
        }
        return;
      }

      if (currentView === "reset_password") {
        Swal.fire({ icon: "success", title: "Password Reset", text: "Login with your new password", confirmButtonColor: "#0B1F33" });
        switchView("login");
        return;
      }

      // --- LOGIC: LOGIN / SIGNUP FLOW ---
      dispatch({ authToken: "mock_auth_token_123" });
      onClose();
      Swal.fire({
        icon: "success",
        title: currentView === "signup" ? "Account Created!" : "Welcome Back!",
        text: "Successfully accessed Eventix.",
        confirmButtonColor: "#0B1F33",
        timer: 1500,
      });
      navigate("/dashboard");

    }, 1500);
  };

  if (!isOpen) return null;

  // Header Titles based on View
  const getHeaderContent = () => {
    switch (currentView) {
      case "signup": return { title: "Join Eventix", sub: "Create an account to get started." };
      case "forgot_email": return { title: "Reset Password", sub: "Enter your email to receive an OTP." };
      case "verify_otp": return { title: "Verify OTP", sub: `Enter the code sent to ${recoveryEmail}` };
      case "reset_password": return { title: "New Password", sub: "Create a strong password." };
      default: return { title: "Welcome back", sub: "Plan your next premium event." };
    }
  };

  const headerData = getHeaderContent();

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-6 font-sans">

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0B1F33]/80 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all animate-fade-in-up border border-gray-100">

        {/* Close Button */}
        <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-[#0B1F33] hover:bg-gray-100 p-2 rounded-full transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold tracking-tight text-[#0B1F33]">
            {headerData.title}
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            {headerData.sub}
          </p>
        </div>

        {/* --- GOOGLE LOGIN (Only for Login/Signup) --- */}
        {(currentView === "login" || currentView === "signup") && (
          <>
            <button
              onClick={handleGoogleLogin}
              disabled={loader}
              className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 hover:bg-gray-50 text-[#0B1F33] font-semibold py-3 px-4 rounded-lg transition-all duration-200 group active:scale-[0.98]"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              {currentView === "login" ? "Sign in with Google" : "Sign up with Google"}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-2 text-xs text-gray-400 uppercase tracking-wide">
                  Or continue with email
                </span>
              </div>
            </div>
          </>
        )}

        {/* --- DYNAMIC FORM --- */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          {/* 1. Full Name (Signup Only) */}
          {currentView === "signup" && (
            <div>
              <label className="block text-sm font-semibold text-[#0B1F33] mb-1">Full Name</label>
              <input
                type="text"
                {...register("name")}
                className={`w-full rounded-lg border bg-gray-50 px-4 py-2.5 text-[#0B1F33] focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all ${errors.name ? 'border-red-300 focus:ring-red-200' : 'border-gray-200'}`}
                placeholder="Rahul Biswas"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
            </div>
          )}

          {/* 2. Email (Login, Signup, Forgot Email) */}
          {(currentView === "login" || currentView === "signup" || currentView === "forgot_email") && (
            <div>
              <label className="block text-sm font-semibold text-[#0B1F33] mb-1">Email Address</label>
              <input
                type="email"
                {...register("email")}
                className={`w-full rounded-lg border bg-gray-50 px-4 py-2.5 text-[#0B1F33] focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all ${errors.email ? 'border-red-300 focus:ring-red-200' : 'border-gray-200'}`}
                placeholder="name@example.com"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>
          )}

          {/* 3. OTP Input (Verify OTP Only) */}
          {currentView === "verify_otp" && (
            <div>
              <label className="block text-sm font-semibold text-[#0B1F33] mb-1">Enter OTP</label>
              <input
                type="text"
                maxLength={6}
                {...register("otp")}
                className={`w-full rounded-lg border bg-gray-50 px-4 py-2.5 text-[#0B1F33] tracking-[0.5em] text-center font-bold text-lg focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all ${errors.otp ? 'border-red-300 focus:ring-red-200' : 'border-gray-200'}`}
                placeholder="------"
              />
              {errors.otp && <p className="mt-1 text-xs text-red-500">{errors.otp.message}</p>}
              <div className="text-center mt-2">
                <button type="button" onClick={() => switchView("forgot_email")} className="text-xs text-[#D4AF37] hover:underline">Wrong Email? Resend</button>
              </div>
            </div>
          )}

          {/* 4. Password & Confirm (Login, Signup, Reset) */}
          {(currentView === "login" || currentView === "signup" || currentView === "reset_password") && (
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-semibold text-[#0B1F33]">
                  {currentView === "reset_password" ? "New Password" : "Password"}
                </label>
                {currentView === "login" && (
                  <button type="button" onClick={() => switchView("forgot_email")} className="text-xs font-semibold text-[#D4AF37] hover:underline">
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  {...register("password")}
                  className={`w-full rounded-lg border bg-gray-50 px-4 py-2.5 text-[#0B1F33] focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all pr-10 ${errors.password ? 'border-red-300 focus:ring-red-200' : 'border-gray-200'}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#0B1F33]"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
            </div>
          )}

          {/* 5. Confirm Password (Reset Password Only) */}
          {currentView === "reset_password" && (
            <div>
              <label className="block text-sm font-semibold text-[#0B1F33] mb-1">Confirm Password</label>
              <input
                type="password"
                {...register("confirmPassword")}
                className={`w-full rounded-lg border bg-gray-50 px-4 py-2.5 text-[#0B1F33] focus:ring-2 focus:ring-[#D4AF37] outline-none transition-all ${errors.confirmPassword ? 'border-red-300 focus:ring-red-200' : 'border-gray-200'}`}
                placeholder="••••••••"
              />
              {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>}
            </div>
          )}

          {/* --- PRIMARY ACTION BUTTON --- */}
          <button
            type="submit"
            disabled={loader}
            className="w-full flex justify-center items-center gap-2 rounded-lg bg-[#D4AF37] px-8 py-3 text-base font-bold text-[#0B1F33] shadow-lg shadow-[#D4AF37]/20 hover:bg-[#c5a02e] hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loader ? (
              <div className="w-5 h-5 border-2 border-[#0B1F33] border-t-transparent rounded-full animate-spin"></div>
            ) : (
              // Dynamic Button Label
              currentView === "login" ? "Sign In" :
                currentView === "signup" ? "Create Account" :
                  currentView === "forgot_email" ? "Send OTP" :
                    currentView === "verify_otp" ? "Verify Code" : "Reset Password"
            )}
          </button>
        </form>

        {/* Footer Toggle (Back to Login) */}
        <div className="mt-6 text-center pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            {currentView === "login" ? "Don't have an account? " :
              currentView === "signup" ? "Already have an account? " :
                "Remember your password? "}

            <button
              onClick={() => switchView(currentView === "login" ? "signup" : "login")}
              className="font-bold text-[#0B1F33] hover:text-[#D4AF37] transition-colors underline decoration-dotted underline-offset-2"
            >
              {currentView === "login" ? "Register now" : "Log In"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;